/* Silver & Co — comportamiento base. Web components nativos, sin dependencias. */

/* ---------- animación de entrada ---------- */
(() => {
  const todas = () => document.querySelectorAll('[data-anim]:not(.visible)')
    .forEach((el) => el.classList.add('visible'));
  if (!('IntersectionObserver' in window)) { todas(); return; }
  const io = new IntersectionObserver((entradas) => {
    entradas.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('visible');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
  const observar = () => document.querySelectorAll('[data-anim]:not(.visible)').forEach((el) => io.observe(el));
  observar();
  document.addEventListener('shopify:section:load', observar);
  // por si el observador nunca dispara (pestaña en segundo plano, captura de
  // pantalla automática, navegador raro): a los 2 s se muestra todo igual
  setTimeout(todas, 2000);
})();

/* ---------- sombra del header al scrollear ---------- */
(() => {
  const header = document.querySelector('[data-header]');
  if (!header) return;
  const alScroll = () => header.dataset.scrolled = window.scrollY > 8 ? 'true' : 'false';
  alScroll();
  addEventListener('scroll', alScroll, { passive: true });
})();

/* ---------- Drawer: menú móvil, carrito, cualquier panel lateral ---------- */
class SilverDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (e) => {
      if (e.target.closest('[data-cerrar]') || e.target.classList.contains('drawer__fondo')) this.cerrar();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.abierto) this.cerrar(); });
    document.querySelectorAll(`[data-abrir="${this.id}"]`).forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); this.abrir(); })
    );
  }
  get abierto() { return this.classList.contains('abierto'); }
  abrir() {
    this._ultimoFoco = document.activeElement;
    this.classList.add('abierto');
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => this.querySelector('[data-cerrar]')?.focus());
    this.addEventListener('keydown', this._trampa);
  }
  cerrar() {
    this.classList.remove('abierto');
    document.body.style.overflow = '';
    this.removeEventListener('keydown', this._trampa);
    this._ultimoFoco?.focus();
  }
  _trampa = (e) => {
    if (e.key !== 'Tab') return;
    const f = [...this.querySelectorAll('a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])')]
      .filter((el) => el.offsetParent !== null);
    if (!f.length) return;
    const [primero, ultimo] = [f[0], f[f.length - 1]];
    if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
    else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
  };
}
customElements.define('silver-drawer', SilverDrawer);

/* ---------- Carrito: agregar, actualizar, refrescar el drawer ---------- */
const ev = (n, d) => window.SilverAnalytics && window.SilverAnalytics.evento(n, d);

class SilverCarrito {
  static async agregar(formData) {
    const r = await fetch(`${window.Silver.routes.cart_add}.js`, {
      method: 'POST', headers: { Accept: 'application/json' }, body: formData
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || data.message);
    ev('agregar_carrito', {
      valor: (data.final_line_price != null ? data.final_line_price : data.price * data.quantity) / 100,
      items: [{ sku: data.sku, nombre: data.product_title, categoria: data.product_type,
                variante: data.variant_title, precio: data.price / 100, cantidad: data.quantity }]
    });
    await SilverCarrito.refrescar();
    document.getElementById('cart-drawer')?.abrir();
    return data;
  }
  static async cambiar(id, cantidad) {
    const r = await fetch(`${window.Silver.routes.cart_change}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity: cantidad })
    });
    await SilverCarrito.refrescar();
    return r.json();
  }
  static async refrescar() {
    const [carrito, html] = await Promise.all([
      fetch(`${window.Silver.routes.cart}.js`).then((r) => r.json()),
      fetch(`${window.Silver.routes.cart}?section_id=cart-drawer`).then((r) => r.text())
    ]);
    const nuevo = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-contenido]');
    const actual = document.querySelector('[data-cart-contenido]');
    if (nuevo && actual) actual.innerHTML = nuevo.innerHTML;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = carrito.item_count;
      el.hidden = carrito.item_count === 0;
    });
    document.dispatchEvent(new CustomEvent('silver:carrito', { detail: carrito }));
    return carrito;
  }
}
window.SilverCarrito = SilverCarrito;

/* ---------- iniciar checkout ---------- */
document.addEventListener('click', (e) => {
  if (!e.target.closest('[name="checkout"]')) return;
  fetch(`${window.Silver.routes.cart}.js`).then(r => r.json()).then(c => {
    ev('iniciar_checkout', {
      valor: c.total_price / 100,
      items: c.items.map(i => ({ sku: i.sku, nombre: i.product_title, categoria: i.product_type,
                                 variante: i.variant_title, precio: i.price / 100, cantidad: i.quantity }))
    });
  });
});

/* ---------- Formulario de producto ---------- */
class SilverProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.boton = this.querySelector('[type="submit"]');
    this.form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      const textoOriginal = this.boton.textContent;
      const botones = this.form.id === 'form-producto' ? [...document.querySelectorAll('[data-agregar]')] : [this.boton];
      botones.forEach((boton) => {
        boton.disabled = true;
        boton.textContent = window.Silver.i18n.agregando;
      });
      try {
        await SilverCarrito.agregar(new FormData(this.form));
      } catch (err) {
        botones.forEach((boton) => { boton.textContent = err.message; });
        setTimeout(() => botones.forEach((boton) => { boton.textContent = textoOriginal; }), 2600);
        botones.forEach((boton) => { boton.disabled = false; });
        return;
      }
      botones.forEach((boton) => {
        boton.textContent = textoOriginal;
        boton.disabled = false;
      });
    });
  }
}
customElements.define('silver-product-form', SilverProductForm);

/* ---------- Selector de variantes ---------- */
class SilverVariantes extends HTMLElement {
  connectedCallback() {
    const datos = this.querySelector('[type="application/json"]');
    if (!datos) return;
    this.variantes = JSON.parse(datos.textContent);
    this.addEventListener('change', () => this.actualizar());
  }
  get seleccion() {
    return [...this.querySelectorAll('input[type="radio"]:checked, select')].map((i) => i.value);
  }
  actualizar() {
    const sel = this.seleccion;
    const v = this.variantes.find((x) => x.options.every((o, i) => o === sel[i]));
    const input = document.querySelector('[data-variant-id]');
    const botones = [...document.querySelectorAll('[data-agregar]')];
    const precio = document.querySelector('[data-precio]');

    if (!v) {
      botones.forEach((boton) => {
        boton.disabled = true;
        boton.textContent = window.Silver.i18n.no_disponible;
      });
      return;
    }
    if (input) input.value = v.id;
    botones.forEach((boton) => {
      boton.disabled = !v.available;
      boton.textContent = v.available ? window.Silver.i18n.agregar : window.Silver.i18n.agotado;
    });

    if (precio) {
      fetch(`${window.location.pathname}?variant=${v.id}&section_id=main-product`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const nuevo = doc.querySelector('[data-precio]');
          if (nuevo) {
            precio.innerHTML = nuevo.innerHTML;
            const sticky = document.querySelector('[data-sticky-price]');
            if (sticky) sticky.textContent = nuevo.textContent.trim();
          }
          const stock = doc.querySelector('[data-stock]');
          if (stock) document.querySelector('[data-stock]')?.replaceWith(stock);
        });
    }
    if (v.featured_media) {
      document.querySelector(`[data-media-id="${v.featured_media.id}"]`)?.click();
    }
    window.history.replaceState({}, '', `${window.location.pathname}?variant=${v.id}`);
  }
}
customElements.define('silver-variantes', SilverVariantes);

/* ---------- Galería ---------- */
class SilverGaleria extends HTMLElement {
  connectedCallback() {
    this.principal = this.querySelector('[data-galeria-principal]');
    this.querySelectorAll('[data-media-id]').forEach((tira) => {
      tira.addEventListener('click', () => {
        this.principal.querySelector('img').src = tira.dataset.grande;
        this.principal.querySelector('img').srcset = tira.dataset.grandeSrcset || '';
        this.querySelectorAll('[data-media-id]').forEach((t) => t.setAttribute('aria-current', 'false'));
        tira.setAttribute('aria-current', 'true');
      });
    });
    this.principal?.addEventListener('click', () => this.principal.classList.toggle('zoom'));
  }
}
customElements.define('silver-galeria', SilverGaleria);

document.addEventListener('click', (e) => {
  const boton = e.target.closest('[data-video-sonido]');
  if (!boton) return;
  const video = boton.closest('.video-silverco')?.querySelector('video');
  if (!video) return;
  video.muted = !video.muted;
  video.play().catch(() => {});
  boton.setAttribute('aria-pressed', String(!video.muted));
  boton.setAttribute('aria-label', video.muted ? boton.dataset.sonidoOff : boton.dataset.sonidoOn);
});

/* ---------- Cantidad ---------- */
class SilverCantidad extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.querySelector('[data-menos]')?.addEventListener('click', () => this.ajustar(-1));
    this.querySelector('[data-mas]')?.addEventListener('click', () => this.ajustar(1));
    this.input.addEventListener('change', () => this.emitir());
  }
  ajustar(d) {
    const v = Math.max(parseInt(this.input.min || 0, 10), parseInt(this.input.value, 10) + d);
    this.input.value = v;
    this.emitir();
  }
  emitir() {
    const linea = this.dataset.linea;
    if (linea) SilverCarrito.cambiar(this.dataset.key, parseInt(this.input.value, 10));
  }
}
customElements.define('silver-cantidad', SilverCantidad);

/* ---------- Buscador predictivo ---------- */
class SilverBuscador extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.resultados = this.querySelector('[data-resultados]');
    if (!this.input) return;
    this.input.addEventListener('input', () => {
      clearTimeout(this._t);
      this._t = setTimeout(() => this.buscar(), 260);
    });
    document.querySelectorAll('[data-abrir-buscador]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); this.abrir(); })
    );
    this.querySelector('[data-cerrar]')?.addEventListener('click', () => this.cerrar());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.cerrar(); });
  }
  abrir() { this.classList.add('abierto'); document.body.style.overflow = 'hidden'; this.input?.focus(); }
  cerrar() { this.classList.remove('abierto'); document.body.style.overflow = ''; }
  async buscar() {
    const q = this.input.value.trim();
    if (q.length < 2) { this.resultados.innerHTML = ''; return; }
    const url = `${window.Silver.routes.predictive_search}?q=${encodeURIComponent(q)}`
      + `&resources[type]=product,collection&resources[limit]=8&section_id=predictive-search`;
    const html = await fetch(url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    this.resultados.innerHTML = doc.querySelector('[data-predictive]')?.innerHTML || '';
  }
}
customElements.define('silver-buscador', SilverBuscador);

/* ---------- Favoritos (guardados en el navegador de cada visitante) ---------- */
(() => {
  const CLAVE = 'silverco_favoritos';
  const leer = () => { try { return JSON.parse(localStorage.getItem(CLAVE) || '[]'); } catch (e) { return []; } };
  const escribir = (l) => { try { localStorage.setItem(CLAVE, JSON.stringify(l)); } catch (e) {} };

  const pintar = () => {
    const favs = leer();
    document.querySelectorAll('[data-favorito]').forEach((b) => {
      b.setAttribute('aria-pressed', favs.includes(b.dataset.favorito) ? 'true' : 'false');
    });
  };

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-favorito]');
    if (!b) return;
    e.preventDefault();
    const h = b.dataset.favorito, favs = leer(), i = favs.indexOf(h);
    if (i === -1) favs.push(h); else favs.splice(i, 1);
    escribir(favs);
    pintar();
  });

  document.addEventListener('DOMContentLoaded', pintar);
  // las secciones se vuelven a pintar solas en el editor de temas
  document.addEventListener('shopify:section:load', pintar);
  pintar();
})();
